import { create } from 'zustand';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import {
  dynamicPricingService,
  type CorridaPedidaDeTodas,
  type ProductsFilters,
  type ResumenDeTodas,
} from '../services/dynamic-pricing-service';
import type {
  DynamicPricingTab,
  DynamicProduct,
  CompetitorStore,
  CompetitorStoreRef,
  LinkState,
  DataState,
  ProductFilterCounts,
  CompetitorProduct,
  MissingCompetitorItem,
  PriceAlert,
  DynamicPriceLog,
  DynamicPricingStatus,
  ScrapeResult,
  CreateProductInput,
  CreateCompetitorInput,
  CreateCompetitorProductInput,
  ScrapedCatalogJob,
  ScrapedCatalogItem,
  CatalogScrapeProgress,
  MatchSuggestion,
  InferenceStats,
  InferenceResult,
  CorridaDeInferencia,
  ImportCsvResult,
  ProductoGuardado,
  MonitorRun,
  MonitorRunsFilters,
  ScheduledTask,
  ScheduledTaskAgendaItem,
  CreateScheduledTaskInput,
} from '../types/dynamic-pricing.types';

interface DynamicPricingState {
  // UI
  activeTab: DynamicPricingTab;
  setActiveTab: (tab: DynamicPricingTab) => void;
  alertsPrefilterStoreId: string | null;
  navigateToAlertsForStore: (storeId: string) => void;
  /**
   * Ronda F de L4 (CAT-1): «Vinculados» de Catálogo abre Productos con los vinculados de
   * esa tienda. Productos guarda sus filtros en estado propio: lo lee al montarse y lo
   * borra (como `alertsPrefilterStoreId` con Monitoreo).
   */
  productsPrefilter: { storeId: string; storeName: string } | null;
  navigateToProductsLinkedWith: (storeId: string, storeName: string) => void;
  /** Desde el contador del encabezado: todas las alertas, sin filtro de tienda. */
  navigateToAlerts: () => void;

  // Products
  products: DynamicProduct[];
  productsTotal: number;
  productsPage: number;
  productsTotalPages: number;
  productsSearch: string;
  productsActiveFilter: string;
  /** Los filtros de la última consulta de la lista (L0, PD-05). */
  productsFilters: ProductsFilters;
  /** El orden de la última consulta (L4, A-13: se perdía en cada recarga). */
  productsSort: { sortBy?: string; sortDir?: 'asc' | 'desc' };
  /** Vuelve a pedir la lista con los MISMOS filtros y el mismo orden. */
  reloadProducts: (page?: number) => Promise<void>;
  productsCompetitorStores: CompetitorStoreRef[];
  /** Números de los chips de filtro. null mientras no llegaron o si falló el pedido. */
  productFilterCounts: ProductFilterCounts | null;
  isLoadingProducts: boolean;
  setProductsSearch: (search: string) => void;
  setProductsActiveFilter: (filter: string) => void;
  fetchProducts: (params?: {
    search?: string;
    page?: number;
    isActive?: string;
    sortByStore?: string;
    sortDir?: 'asc' | 'desc';
    sortBy?: string;
    hasCalculatedPrice?: boolean;
    onlyWithCompetitorPrice?: boolean;
    onlyOnOffer?: boolean;
    onlyLowConfidence?: boolean;
    onlyWithError?: boolean;
    withoutCompetitor?: boolean;
    linkState?: LinkState;
    dataState?: DataState;
    category?: string;
    priceMin?: number;
    priceMax?: number;
    linkedStoreId?: string;
  }) => Promise<void>;
  createProduct: (data: CreateProductInput) => Promise<void>;
  updateProduct: (
    id: string,
    // Lote C2b: `motivo` va al registro del cambio de precio, no al producto.
    data: Partial<CreateProductInput> & { sacarDeEstrategias?: boolean; motivo?: string }
  ) => Promise<ProductoGuardado>;
  /** L4 (D-L0-1): dar de baja en vez de borrar; devuelve cuántos (0 si no se pudo). */
  bulkDeactivateProducts: (ids: string[]) => Promise<number>;
  bulkDeactivateAllProducts: (expectedCount: number) => Promise<number>;
  importProductsCsv: (file: File) => Promise<ImportCsvResult>;
  calculateProduct: (id: string) => Promise<void>;
  calculateAll: () => Promise<void>;
  isCalculating: boolean;

  // Competitors
  competitors: CompetitorStore[];
  isLoadingCompetitors: boolean;
  /** `silent`: sin poner la lista «cargando» (no desmonta las tarjetas; revisión T6+T7). */
  fetchCompetitors: (opciones?: { silent?: boolean }) => Promise<void>;
  createCompetitor: (data: CreateCompetitorInput) => Promise<void>;
  deleteCompetitor: (id: string) => Promise<void>;
  resetCompetitorStore: (id: string) => Promise<void>;
  toggleCompetitorMonitoring: (id: string, enabled: boolean) => Promise<void>;
  updateMonitoringConfig: (
    id: string,
    config: { priceCheckIntervalHours: number; rescrapeIntervalDays: number }
  ) => Promise<void>;
  updateCompetitorScraperType: (id: string, scraperType: string) => Promise<void>;
  toggleCompetitorAnalysis: (id: string, includeInAnalysis: boolean) => Promise<void>;
  missingItems: MissingCompetitorItem[];
  isLoadingMissingItems: boolean;
  fetchMissingItems: (storeId: string) => Promise<void>;

  // Competitor Products
  competitorProducts: CompetitorProduct[];
  isLoadingCompetitorProducts: boolean;
  fetchCompetitorProducts: (productId: string) => Promise<void>;
  createCompetitorProduct: (data: CreateCompetitorProductInput) => Promise<void>;
  deleteCompetitorProduct: (id: string) => Promise<void>;

  // Scraping
  isScraping: boolean;
  lastScrapeResult: ScrapeResult | null;
  scrapeAll: () => Promise<ScrapeResult | null>;
  clearScrapeResult: () => void;

  // Manual price entry
  updateCompetitorProductPrice: (id: string, price: number) => Promise<void>;

  // Logs
  logs: DynamicPriceLog[];
  isLoadingLogs: boolean;
  fetchLogs: () => Promise<void>;

  // Status
  status: DynamicPricingStatus | null;
  fetchStatus: () => Promise<void>;

  // Catalog
  catalogJobs: ScrapedCatalogJob[];
  isLoadingCatalogJobs: boolean;
  fetchCatalogJobs: () => Promise<void>;
  activeScrapeJobId: string | null;
  scrapeProgress: CatalogScrapeProgress | null;
  startCatalogScrape: (
    storeId: string,
    scraperType?: string,
    proxyUrl?: string,
    cleanScrape?: boolean
  ) => Promise<string | null>;
  /** Rechaza con el error del servidor (el diálogo muestra su frase). */
  startOwnStoreScrape: (
    url: string,
    scraperType?: string,
    proxyUrl?: string
  ) => Promise<string | null>;
  cancelCatalogScrape: (jobId: string) => Promise<void>;
  resumeCatalogScrape: (jobId: string) => Promise<string | null>;
  deleteCatalogJob: (jobId: string) => Promise<void>;
  pollScrapeProgress: (jobId: string) => Promise<void>;
  catalogItems: ScrapedCatalogItem[];
  catalogItemsTotal: number;
  catalogItemsPage: number;
  catalogItemsTotalPages: number;
  catalogCategories: string[];
  isLoadingCatalogItems: boolean;
  selectedJobId: string | null;
  setSelectedJobId: (jobId: string | null) => void;
  fetchCatalogItems: (params?: {
    jobId?: string;
    status?: string;
    search?: string;
    category?: string;
    page?: number;
  }) => Promise<void>;
  matchCatalogItem: (itemId: string, productId: string) => Promise<void>;
  ignoreCatalogItem: (itemId: string, ignored: boolean) => Promise<void>;
  importCatalogItemsAsProducts: (
    itemIds: string[],
    strategy?: string
  ) => Promise<{ created: number; skipped: number }>;
  importAllJobItemsAsProducts: (
    jobId: string,
    strategy?: string,
    strategyConfig?: Record<string, any>,
    replaceAll?: boolean
  ) => Promise<{ created: number; skipped: number; total: number }>;

  // Inference
  suggestions: MatchSuggestion[];
  suggestionTotal: number;
  suggestionPage: number;
  suggestionTotalPages: number;
  isLoadingSuggestions: boolean;
  isRunningInference: boolean;
  lastInferenceResult: InferenceResult | null;
  inferenceJobId: string | null;
  setInferenceJobId: (jobId: string | null) => void;
  runInference: (
    jobId: string,
    opts?: { minConfidence?: number; replaceExisting?: boolean }
  ) => Promise<InferenceResult | null>;
  /**
   * Ronda L2-E (ítem 1): si la tienda del catálogo tiene una corrida en cola o
   * calculando, la pantalla se engancha a ella (la pestaña reabierta muestra la barra
   * sola). Sin corrida, o si ya hay una espera, no hace nada.
   */
  retomarCorrida: (jobId: string) => Promise<InferenceResult | null>;
  fetchSuggestions: (params?: {
    jobId?: string;
    status?: string;
    minConfidence?: number;
    maxConfidence?: number;
    page?: number;
    limit?: number;
    q?: string;
  }) => Promise<void>;
  acceptSuggestion: (
    id: string,
    opciones?: { enLote?: boolean }
  ) => Promise<{ siblingsRejected: number }>;
  rejectSuggestion: (
    id: string,
    motivo?: 'NINGUNO_ES',
    opciones?: { enLote?: boolean }
  ) => Promise<void>;

  // Inferencia — los números del embudo. La lista vive en la pantalla: es de
  // una sola pestaña y con estado local no hay dos copias que sincronizar.
  inferenceStats: InferenceStats | null;
  isLoadingStats: boolean;
  fetchInferenceStats: (jobId: string) => Promise<void>;
  cleanInference: (jobId: string) => Promise<boolean>;
  cleanStoreInference: (storeId: string) => Promise<boolean>;
  fixJobPrices: (jobId: string) => Promise<boolean>;
  cancelInference: () => void;

  // Monitor Runs
  monitorRuns: MonitorRun[];
  monitorRunsTotal: number;
  monitorRunsPage: number;
  monitorRunsTotalPages: number;
  isLoadingMonitorRuns: boolean;
  monitorRunsFilters: MonitorRunsFilters;
  setMonitorRunsFilters: (filters: Partial<MonitorRunsFilters>) => void;
  fetchMonitorRuns: (params?: MonitorRunsFilters, opts?: { silent?: boolean }) => Promise<void>;
  selectedMonitorRun: MonitorRun | null;
  selectedMonitorRunAlerts: PriceAlert[];
  isLoadingMonitorRunDetail: boolean;
  fetchMonitorRunDetail: (id: string) => Promise<void>;
  clearMonitorRunDetail: () => void;
  isTriggeringMonitorRun: boolean;
  /** El mensaje del servidor (dice qué tiendas quedaron afuera), o `null` si no se pudo. */
  triggerMonitorRun: (storeId?: string, parallel?: boolean) => Promise<string | null>;

  // Scheduled Tasks (calendario de scraping)
  scheduledTasks: ScheduledTask[];
  scheduledAgenda: ScheduledTaskAgendaItem[];
  isLoadingScheduledTasks: boolean;
  fetchScheduledData: (days?: number) => Promise<void>;
  createScheduledTask: (data: CreateScheduledTaskInput) => Promise<boolean>;
  updateScheduledTask: (id: string, data: Partial<CreateScheduledTaskInput>) => Promise<boolean>;
  deleteScheduledTask: (id: string) => Promise<void>;
  runScheduledTaskNow: (id: string) => Promise<boolean>;

  // Error
  error: string | null;
}

/** Fallos de red seguidos que se aguantan antes de dejar de esperar. */
const FALLOS_DE_RED_TOLERADOS = 3;
/** Ronda F de L4: número de la última consulta de la lista (ver `fetchProducts`). */
let consultaDeProductos = 0;
const cancelado = () => Object.assign(new Error('cancelado'), { code: 'ERR_CANCELED' });

/**
 * L2 (vault 280): pregunta cada 3 s cómo va una corrida hasta que termina o la
 * persona deja de esperar. Dejar de esperar no la frena: sigue en el servidor y la
 * lista se actualiza al volver a mirarla.
 *
 * Ronda L2-C: cancelar corta también la consulta en vuelo (viaja con la señal) y no
 * deja otra programada; hasta 3 fallos de red seguidos se aguantan (una consulta
 * perdida no es una corrida caída); una respuesta del servidor que no es de red
 * (un 404: la corrida ya no existe) corta de inmediato.
 */
export async function esperarCorrida(
  runId: string,
  signal: AbortSignal,
  cadaMs = 3000
): Promise<CorridaDeInferencia> {
  let fallosSeguidos = 0;
  for (;;) {
    if (signal.aborted) throw cancelado();
    try {
      const c = await dynamicPricingService.getInferenceRun(runId, signal);
      fallosSeguidos = 0;
      if (signal.aborted) throw cancelado();
      if (c.estado === 'LISTO' || c.estado === 'FALLO') return c;
    } catch (err: any) {
      if (signal.aborted || err?.code === 'ERR_CANCELED') throw cancelado();
      const esDeRed = !err?.response;
      fallosSeguidos += 1;
      if (!esDeRed || fallosSeguidos > FALLOS_DE_RED_TOLERADOS) throw err;
    }
    await new Promise<void>((ok, mal) => {
      if (signal.aborted) {
        mal(cancelado());
        return;
      }
      const alCancelar = () => {
        clearTimeout(t);
        mal(cancelado());
      };
      const t = setTimeout(() => {
        signal.removeEventListener('abort', alCancelar);
        ok();
      }, cadaMs);
      signal.addEventListener('abort', alCancelar, { once: true });
    });
  }
}

/** Espera una corrida de retail y la traduce a la forma de siempre del resultado. */
async function resultadoDeLaCorrida(runId: string, signal: AbortSignal): Promise<InferenceResult> {
  const corrida = await esperarCorrida(runId, signal);
  if (corrida.estado === 'FALLO') {
    // La pantalla muestra esta frase tal cual (ronda L2-C, ítem 45).
    throw {
      response: { data: { error: corrida.error ?? 'La comparación no pudo terminar.' } },
    };
  }
  return {
    analyzed: corrida.total ?? 0,
    generated: corrida.generadas ?? 0,
    highConfidence: corrida.seguras ?? 0,
    mediumConfidence: 0,
    lowConfidence: 0,
    skipped: 0,
    totalCatalogItems: corrida.total ?? 0,
    ownProductCount: 0,
    unmatchedItems: [],
  };
}

/**
 * Una espera de corrida (pedida o retomada): una sola a la vez, y sólo quien sigue
 * siendo la espera actual toca el estado (ronda L2-C, ítem 28).
 */
async function esperarComoActual(
  set: (parcial: Record<string, unknown>) => void,
  get: () => any,
  obtener: (signal: AbortSignal) => Promise<InferenceResult>
): Promise<InferenceResult | null> {
  get()._inferenceAbort?.abort();
  const abort = new AbortController();
  const esLaActual = () => get()._inferenceAbort === abort;
  set({ isRunningInference: true, lastInferenceResult: null, error: null, _inferenceAbort: abort });
  try {
    const result = await obtener(abort.signal);
    if (!esLaActual()) return null;
    set({ isRunningInference: false, lastInferenceResult: result, _inferenceAbort: null });
    // Cargar sugerencias automáticamente al terminar — no depende del componente
    const { fetchSuggestions, inferenceJobId } = get();
    if (inferenceJobId) {
      fetchSuggestions({ jobId: inferenceJobId, status: 'PENDING', page: 1 });
    }
    return result;
  } catch (err: any) {
    if (err?.code === 'ERR_CANCELED' || abort.signal.aborted) {
      if (esLaActual()) set({ isRunningInference: false, _inferenceAbort: null });
      return null;
    }
    if (!esLaActual()) return null;
    set({
      error: err?.response?.data?.error || 'La comparación no pudo terminar.',
      isRunningInference: false,
      _inferenceAbort: null,
    });
    return null;
  }
}

/**
 * Ronda L2-D (ítem 51): «Comparar contra todos» encola una corrida por tienda y la
 * pantalla espera cada una, de a una (el worker también las corre de a una: preguntar
 * por todas a la vez sólo multiplicaría las consultas). Una que falló, o que no se
 * pudo pedir, queda en el resumen con su frase; dejar de esperar corta todo.
 */
export async function esperarCorridas(
  corridas: CorridaPedidaDeTodas[],
  signal: AbortSignal,
  cadaMs = 3000
): Promise<ResumenDeTodas> {
  const detalle: ResumenDeTodas['detalle'] = [];
  for (const c of corridas) {
    const base = { jobId: c.jobId, tienda: c.tienda, propuestas: 0, automaticas: 0 };
    if (!c.runId) {
      detalle.push({ ...base, error: c.error ?? 'No se pudo pedir la comparación.' });
      continue;
    }
    try {
      const fin = await esperarCorrida(c.runId, signal, cadaMs);
      if (fin.estado === 'FALLO') {
        detalle.push({ ...base, error: fin.error ?? 'La comparación no pudo terminar.' });
      } else {
        detalle.push({
          ...base,
          propuestas: fin.generadas ?? 0,
          automaticas: fin.seguras ?? 0,
        });
      }
    } catch (err: any) {
      if (signal.aborted || err?.code === 'ERR_CANCELED') throw cancelado();
      detalle.push({
        ...base,
        error: err?.response?.data?.error ?? 'No se pudo saber cómo terminó la comparación.',
      });
    }
  }
  return {
    tiendas: corridas.length,
    propuestas: detalle.reduce((n, d) => n + d.propuestas, 0),
    automaticas: detalle.reduce((n, d) => n + d.automaticas, 0),
    conError: detalle.filter((d) => d.error).length,
    detalle,
  };
}

export const useDynamicPricingStore = create<DynamicPricingState>((set, get) => ({
  activeTab: 'overview',
  setActiveTab: (tab) => set({ activeTab: tab }),
  alertsPrefilterStoreId: null,
  navigateToAlertsForStore: (storeId) =>
    set({ activeTab: 'monitoring', alertsPrefilterStoreId: storeId }),
  productsPrefilter: null,
  navigateToProductsLinkedWith: (storeId, storeName) =>
    set({ activeTab: 'products', productsPrefilter: { storeId, storeName } }),
  navigateToAlerts: () => set({ activeTab: 'monitoring', alertsPrefilterStoreId: null }),
  // Products
  products: [],
  productsTotal: 0,
  productsPage: 1,
  productsTotalPages: 1,
  productsSearch: '',
  // L4 (D-L0-1): lo dado de baja no aparece salvo que se pida «Inactivos».
  productsActiveFilter: 'true',
  productsFilters: {},
  productsSort: {},
  productsCompetitorStores: [],
  productFilterCounts: null,
  isLoadingProducts: false,
  isCalculating: false,

  setProductsSearch: (search) => set({ productsSearch: search }),
  setProductsActiveFilter: (filter) => set({ productsActiveFilter: filter }),

  fetchProducts: async (params: any = {}) => {
    // Ronda F de L4: si llega tarde la respuesta de una consulta vieja, no pisa la lista
    // de la más nueva (el chip marcado con la lista sin filtrar).
    const consulta = ++consultaDeProductos;
    set({ isLoadingProducts: true, error: null });
    try {
      const state = get();
      const search = params.search ?? state.productsSearch;
      const isActive = params.isActive ?? state.productsActiveFilter;
      const page = params.page ?? 1;
      // L0 (vault 272, PD-05): los filtros se guardan tal cual se pidieron.
      // «Seleccionar los N + Eliminar» manda ESTOS, no una versión recortada, y los
      // refrescos internos del store los vuelven a pasar (ruling 5-2).
      const filtros: ProductsFilters = {
        search: search || undefined,
        isActive: isActive || undefined,
        sortByStore: params.sortByStore || undefined,
        hasCalculatedPrice: params.hasCalculatedPrice || undefined,
        onlyWithCompetitorPrice: params.onlyWithCompetitorPrice || undefined,
        onlyOnOffer: params.onlyOnOffer || undefined,
        onlyLowConfidence: params.onlyLowConfidence || undefined,
        onlyWithError: params.onlyWithError || undefined,
        withoutCompetitor: params.withoutCompetitor || undefined,
        linkState: params.linkState || undefined,
        dataState: params.dataState || undefined,
        category: params.category || undefined,
        priceMin: params.priceMin ?? undefined,
        priceMax: params.priceMax ?? undefined,
        linkedStoreId: params.linkedStoreId || undefined,
      };
      const data = await dynamicPricingService.getProducts({
        ...filtros,
        page,
        limit: 50,
        sortDir: params.sortDir || undefined,
        sortBy: params.sortBy || undefined,
      });
      if (consulta !== consultaDeProductos) return;

      set({
        products: data.products,
        productsTotal: data.total,
        productsPage: data.page,
        productsTotalPages: data.totalPages,
        productsCompetitorStores: data.competitorStores,
        productsFilters: filtros,
        productsSort: { sortBy: params.sortBy || undefined, sortDir: params.sortDir || undefined },
        isLoadingProducts: false,
      });

      // Los conteos de los chips van por separado: son seis COUNT sobre toda la tabla y
      // no tienen por qué demorar la lista. Van con los mismos filtros que la lista,
      // incluidos el orden por tienda y «solo con precio» (C-2): si no, un chip dice un
      // número sobre otra base que la que se está mirando. Si fallan, los chips se
      // quedan sin número pero los filtros siguen andando.
      dynamicPricingService
        .getProductFilterCounts({
          search: search || undefined,
          isActive: isActive || undefined,
          sortByStore: params.sortByStore || undefined,
          hasCalculatedPrice: params.hasCalculatedPrice || undefined,
          onlyWithCompetitorPrice: params.onlyWithCompetitorPrice || undefined,
          onlyOnOffer: params.onlyOnOffer || undefined,
          onlyLowConfidence: params.onlyLowConfidence || undefined,
          onlyWithError: params.onlyWithError || undefined,
          withoutCompetitor: params.withoutCompetitor || undefined,
          linkState: params.linkState || undefined,
          dataState: params.dataState || undefined,
          category: params.category || undefined,
          priceMin: params.priceMin ?? undefined,
          priceMax: params.priceMax ?? undefined,
          linkedStoreId: params.linkedStoreId || undefined,
        })
        .then((counts) => {
          if (consulta === consultaDeProductos) set({ productFilterCounts: counts });
        })
        .catch(() => {
          if (consulta === consultaDeProductos) set({ productFilterCounts: null });
        });
    } catch (err: any) {
      if (consulta !== consultaDeProductos) return;
      set({
        error: err?.response?.data?.error || 'Error cargando productos',
        isLoadingProducts: false,
      });
    }
  },

  reloadProducts: async (page) => {
    const s = get();
    await s.fetchProducts({
      ...s.productsFilters,
      ...s.productsSort,
      page: page ?? s.productsPage,
    });
  },

  createProduct: async (data) => {
    set({ error: null });
    try {
      await dynamicPricingService.createProduct(data);
      await get().reloadProducts(1);
      // Ronda F de L7 (punto 3): la cabecera («N productos») sale de `status`.
      void get().fetchStatus();
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando producto' });
      throw err;
    }
  },

  updateProduct: async (id, data) => {
    set({ error: null });
    let guardado: ProductoGuardado;
    try {
      // Plan 4B, S9: el formulario lee `sacadoDe`, `quedanVacias` y `reglas` de la respuesta.
      guardado = await dynamicPricingService.updateProduct(id, data);
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando producto' });
      throw err;
    }
    // El PUT ya guardó. Si después falla refrescar la lista, eso no es «no se guardó»
    // (revisión final, minor 13): se devuelve lo guardado y se marca que la lista quedó
    // vieja. `fetchProducts` guarda su error en `error` en vez de tirar; se miran los dos.
    // Ronda F de L4 (punto 7): dar de baja o reactivar desde la ficha cambia «N productos
    // activos» de la cabecera, que sale de `status` (como crear e importar).
    if ('isActive' in data) void get().fetchStatus();
    try {
      await get().reloadProducts();
    } catch {
      return { ...guardado, listaSinActualizar: true };
    }
    return get().error ? { ...guardado, listaSinActualizar: true } : guardado;
  },

  importProductsCsv: async (file) => {
    set({ error: null });
    try {
      const result = await dynamicPricingService.importProductsCsv(file);
      await get().reloadProducts(1);
      // Ronda F de L7 (punto 3): la cabecera («N productos») sale de `status`.
      void get().fetchStatus();
      return result;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error importando productos' });
      throw err;
    }
  },

  bulkDeactivateProducts: async (ids) => {
    set({ error: null });
    try {
      const result = await dynamicPricingService.bulkDeactivateProducts(ids);
      set({ lastInferenceResult: null });
      // Con los mismos filtros: con «Activos», los dados de baja salen de la lista.
      await get().reloadProducts();
      get().fetchStatus();
      return result.deactivated;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error dando de baja productos' });
      return 0;
    }
  },

  bulkDeactivateAllProducts: async (expectedCount) => {
    set({ error: null });
    try {
      // El servidor cuenta con estos mismos filtros (sólo activos) y, si no da
      // `expectedCount`, no toca nada (409).
      const result = await dynamicPricingService.bulkDeactivateAllProducts(
        get().productsFilters,
        expectedCount
      );
      set({ lastInferenceResult: null });
      await get().reloadProducts(1);
      get().fetchStatus();
      return result.deactivated;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error dando de baja productos' });
      return 0;
    }
  },

  calculateProduct: async (id) => {
    set({ isCalculating: true, error: null });
    try {
      await dynamicPricingService.calculateProduct(id);
      await get().reloadProducts();
      set({ isCalculating: false });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error calculando precio', isCalculating: false });
    }
  },

  calculateAll: async () => {
    set({ isCalculating: true, error: null });
    try {
      await dynamicPricingService.calculateAll();
      await get().reloadProducts();
      set({ isCalculating: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error calculando precios',
        isCalculating: false,
      });
    }
  },

  // Competitors
  competitors: [],
  isLoadingCompetitors: false,
  missingItems: [],
  isLoadingMissingItems: false,

  fetchCompetitors: async (opciones) => {
    const silent = opciones?.silent === true;
    set(silent ? {} : { isLoadingCompetitors: true, error: null });
    try {
      const data = await dynamicPricingService.getCompetitors();
      set({ competitors: data, isLoadingCompetitors: false });
    } catch (err: any) {
      // Un refresco silencioso que falla deja la lista como estaba, sin aviso.
      if (silent) return;
      set({
        error: err?.response?.data?.error || 'Error cargando competidores',
        isLoadingCompetitors: false,
      });
    }
  },

  createCompetitor: async (data) => {
    set({ error: null });
    try {
      await dynamicPricingService.createCompetitor(data);
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando competidor' });
      throw err;
    }
    // Ya se creó: si la lista no se puede recargar, no es «no se pudo agregar» (revisión
    // T8+T9). Se verá en el próximo refresco.
    try {
      const competitors = await dynamicPricingService.getCompetitors();
      set({ competitors });
    } catch {
      /* la tienda quedó creada */
    }
  },

  deleteCompetitor: async (id) => {
    try {
      await dynamicPricingService.deleteCompetitor(id);
      set((s) => ({ competitors: s.competitors.filter((c) => c.id !== id) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando competidor' });
    }
  },

  resetCompetitorStore: async (id) => {
    try {
      await dynamicPricingService.resetCompetitorStore(id);
      // Refrescar la lista para reflejar los timestamps reseteados
      const data = await dynamicPricingService.getCompetitors();
      set({ competitors: data });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error reseteando tienda' });
      throw err;
    }
  },

  toggleCompetitorMonitoring: async (id, enabled) => {
    set({ error: null });
    try {
      await dynamicPricingService.toggleCompetitorMonitoring(id, { enabled });
      set((s) => ({
        competitors: s.competitors.map((c) =>
          c.id === id ? { ...c, priceMonitorEnabled: enabled } : c
        ),
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando monitoreo' });
    }
  },

  updateMonitoringConfig: async (id, config) => {
    set({ error: null });
    try {
      const updated = await dynamicPricingService.updateMonitoringConfig(id, config);
      set((s) => ({
        competitors: s.competitors.map((c) => (c.id === id ? { ...c, ...updated } : c)),
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error guardando configuración' });
      throw err;
    }
  },

  updateCompetitorScraperType: async (id, scraperType) => {
    set({ error: null });
    try {
      await dynamicPricingService.updateCompetitor(id, { scraperType } as any);
      set((s) => ({
        competitors: s.competitors.map((c) => (c.id === id ? { ...c, scraperType } : c)),
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando scraper' });
      throw err;
    }
  },

  toggleCompetitorAnalysis: async (id, includeInAnalysis) => {
    /*
      Optimista: el interruptor se mueve al tocarlo y, si el servidor falla,
      vuelve. Antes esperaba la respuesta y saltaba de golpe un rato después.
    */
    const marcar = (valor: boolean) =>
      set((s) => ({
        competitors: s.competitors.map((c) =>
          c.id === id ? { ...c, includeInAnalysis: valor } : c
        ),
      }));
    set({ error: null });
    marcar(includeInAnalysis);
    try {
      await dynamicPricingService.updateCompetitor(id, { includeInAnalysis });
    } catch (err: any) {
      marcar(!includeInAnalysis);
      set({ error: err?.response?.data?.error || 'Error actualizando la tienda' });
      throw err;
    }
  },

  fetchMissingItems: async (storeId) => {
    set({ isLoadingMissingItems: true });
    try {
      const data = await dynamicPricingService.getMissingItems(storeId);
      set({ missingItems: data, isLoadingMissingItems: false });
    } catch {
      set({ isLoadingMissingItems: false });
    }
  },

  // Competitor Products
  competitorProducts: [],
  isLoadingCompetitorProducts: false,

  fetchCompetitorProducts: async (productId) => {
    set({ isLoadingCompetitorProducts: true, error: null });
    try {
      const data = await dynamicPricingService.getCompetitorProducts(productId);
      set({ competitorProducts: data, isLoadingCompetitorProducts: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando mapeos',
        isLoadingCompetitorProducts: false,
      });
    }
  },

  createCompetitorProduct: async (data) => {
    set({ error: null });
    try {
      await dynamicPricingService.createCompetitorProduct(data);
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando mapeo' });
      throw err;
    }
  },

  deleteCompetitorProduct: async (id) => {
    try {
      await dynamicPricingService.deleteCompetitorProduct(id);
      set((s) => ({ competitorProducts: s.competitorProducts.filter((cp) => cp.id !== id) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando mapeo' });
    }
  },

  // Scraping
  isScraping: false,
  lastScrapeResult: null,
  scrapeAll: async () => {
    set({ isScraping: true, error: null });
    try {
      const result = await dynamicPricingService.scrapeAll();
      set({ isScraping: false, lastScrapeResult: result });
      return result;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error scrapeando', isScraping: false });
      return null;
    }
  },
  clearScrapeResult: () => set({ lastScrapeResult: null }),

  // Manual price entry
  updateCompetitorProductPrice: async (id, price) => {
    set({ error: null });
    try {
      const updated = await dynamicPricingService.updateCompetitorProductPrice(id, price);
      set((s) => ({
        competitorProducts: s.competitorProducts.map((cp) =>
          cp.id === id
            ? {
                ...cp,
                lastScrapedPrice: updated.lastScrapedPrice,
                lastScrapedAt: updated.lastScrapedAt,
              }
            : cp
        ),
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error guardando precio' });
      throw err;
    }
  },

  // Logs
  logs: [],
  isLoadingLogs: false,

  fetchLogs: async () => {
    set({ isLoadingLogs: true, error: null });
    try {
      const logs = await dynamicPricingService.getLogs(100);
      set({ logs, isLoadingLogs: false });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error cargando logs', isLoadingLogs: false });
    }
  },

  // Status
  status: null,
  fetchStatus: async () => {
    try {
      const data = await dynamicPricingService.getStatus();
      set({ status: data });
    } catch {
      /* silent */
    }
  },

  // Catalog
  catalogJobs: [],
  isLoadingCatalogJobs: false,
  activeScrapeJobId: null,
  scrapeProgress: null,
  catalogItems: [],
  catalogItemsTotal: 0,
  catalogItemsPage: 1,
  catalogItemsTotalPages: 1,
  catalogCategories: [],
  isLoadingCatalogItems: false,
  selectedJobId: null,
  setSelectedJobId: (jobId) => set({ selectedJobId: jobId }),

  fetchCatalogJobs: async () => {
    set({ isLoadingCatalogJobs: true, error: null });
    try {
      const data = await dynamicPricingService.getCatalogJobs();
      set({ catalogJobs: data, isLoadingCatalogJobs: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando jobs de catálogo',
        isLoadingCatalogJobs: false,
      });
    }
  },

  startCatalogScrape: async (
    storeId,
    scraperType = 'auto',
    proxyUrl?: string,
    cleanScrape = false
  ) => {
    set({ error: null });
    try {
      const { jobId } = await dynamicPricingService.startCatalogScrape(
        storeId,
        scraperType,
        proxyUrl,
        cleanScrape
      );
      set({ activeScrapeJobId: jobId });
      // Refresh jobs list
      const jobs = await dynamicPricingService.getCatalogJobs();
      set({ catalogJobs: jobs });
      return jobId;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error iniciando scraping' });
      return null;
    }
  },

  startOwnStoreScrape: async (url, scraperType = 'auto', proxyUrl?: string) => {
    try {
      const result = await dynamicPricingService.startOwnStoreScrape(url, scraperType, proxyUrl);
      set({ activeScrapeJobId: result.jobId });
      await get().fetchCatalogJobs();
      return result.jobId;
    } catch (error) {
      // S1b: el error sigue hasta el diálogo, que muestra la frase del servidor (el
      // alta congelada responde 403 con su motivo).
      set({ error: mensajeDeError(error, 'Error iniciando el scraping de tu tienda') });
      throw error;
    }
  },

  cancelCatalogScrape: async (jobId) => {
    try {
      await dynamicPricingService.cancelCatalogScrape(jobId);
      set({ activeScrapeJobId: null, scrapeProgress: null });
      const jobs = await dynamicPricingService.getCatalogJobs();
      set({ catalogJobs: jobs });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error cancelando scraping' });
    }
  },

  resumeCatalogScrape: async (jobId) => {
    set({ error: null });
    try {
      const { jobId: resumedId } = await dynamicPricingService.resumeCatalogScrape(jobId);
      set({ activeScrapeJobId: resumedId });
      const jobs = await dynamicPricingService.getCatalogJobs();
      set({ catalogJobs: jobs });
      return resumedId;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error reanudando scraping' });
      return null;
    }
  },

  deleteCatalogJob: async (jobId) => {
    try {
      await dynamicPricingService.deleteCatalogJob(jobId);
      set((s) => ({
        catalogJobs: s.catalogJobs.filter((j) => j.id !== jobId),
        selectedJobId: s.selectedJobId === jobId ? null : s.selectedJobId,
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando job' });
    }
  },

  pollScrapeProgress: async (jobId) => {
    try {
      const progress = await dynamicPricingService.getCatalogJobProgress(jobId);
      set({ scrapeProgress: progress });

      if (progress.status === 'COMPLETED' || progress.status === 'FAILED') {
        set({ activeScrapeJobId: null });
        // Refresh jobs list
        const jobs = await dynamicPricingService.getCatalogJobs();
        set({ catalogJobs: jobs });
      }
    } catch {
      // Silent — progreso puede no estar disponible todavía
    }
  },

  fetchCatalogItems: async (params = {}) => {
    set({ isLoadingCatalogItems: true, error: null });
    try {
      const data = await dynamicPricingService.getCatalogItems({
        ...params,
        limit: 20,
      });
      set({
        catalogItems: data.items,
        catalogItemsTotal: data.total,
        catalogItemsPage: data.page,
        catalogItemsTotalPages: data.totalPages,
        catalogCategories: data.categories,
        isLoadingCatalogItems: false,
      });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando items',
        isLoadingCatalogItems: false,
      });
    }
  },

  matchCatalogItem: async (itemId, productId) => {
    set({ error: null });
    try {
      const updated = await dynamicPricingService.matchCatalogItem(itemId, productId);
      set((s) => ({
        catalogItems: s.catalogItems.map((item) =>
          item.id === itemId
            ? {
                ...item,
                matchedProductId: updated.matchedProductId,
                matchedProduct: updated.matchedProduct,
                isIgnored: false,
              }
            : item
        ),
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error vinculando producto' });
      throw err;
    }
  },

  ignoreCatalogItem: async (itemId, ignored) => {
    set({ error: null });
    try {
      await dynamicPricingService.ignoreCatalogItem(itemId, ignored);
      set((s) => ({
        catalogItems: s.catalogItems.map((item) =>
          item.id === itemId ? { ...item, isIgnored: ignored } : item
        ),
      }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando item' });
    }
  },

  importCatalogItemsAsProducts: async (itemIds, strategy = 'MATCH_LOWEST') => {
    set({ error: null });
    try {
      const result = await dynamicPricingService.importCatalogItemsAsProducts(itemIds, strategy);
      // Recargar items y jobs para reflejar el nuevo estado
      const jobs = await dynamicPricingService.getCatalogJobs();
      set({ catalogJobs: jobs, lastInferenceResult: null });
      // Forzar recarga de items del job seleccionado
      const state = useDynamicPricingStore.getState();
      if (state.selectedJobId) {
        state.fetchCatalogItems({ jobId: state.selectedJobId });
      }
      get().fetchStatus();
      return result;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error importando productos' });
      throw err;
    }
  },

  importAllJobItemsAsProducts: async (
    jobId,
    strategy = 'MATCH_LOWEST',
    strategyConfig?,
    replaceAll?
  ) => {
    set({ error: null });
    try {
      const result = await dynamicPricingService.importAllJobItemsAsProducts(
        jobId,
        strategy,
        strategyConfig,
        replaceAll
      );
      // Recargar jobs, items y productos
      const jobs = await dynamicPricingService.getCatalogJobs();
      set({ catalogJobs: jobs, lastInferenceResult: null });
      await get().reloadProducts(1);
      const state = useDynamicPricingStore.getState();
      if (state.selectedJobId) {
        state.fetchCatalogItems({ jobId: state.selectedJobId });
      }
      get().fetchStatus();
      return result;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error importando productos' });
      throw err;
    }
  },

  // Inference
  suggestions: [],
  suggestionTotal: 0,
  suggestionPage: 1,
  suggestionTotalPages: 1,
  isLoadingSuggestions: false,
  inferenceStats: null,
  isLoadingStats: false,
  isRunningInference: false,
  lastInferenceResult: null,
  inferenceJobId: null,
  setInferenceJobId: (jobId) => set({ inferenceJobId: jobId }),

  _inferenceAbort: null as AbortController | null,

  runInference: (jobId, opts = {}) =>
    // L2: retail corre en el servidor (con candado por tienda) y se pregunta cómo va.
    // Comida (`runId: null`) sigue con su corrida de siempre: motor congelado.
    esperarComoActual(set as never, get, async (signal) => {
      const pedido = await dynamicPricingService.startInferenceRun(jobId);
      if (!pedido.runId) return dynamicPricingService.runInference(jobId, opts, signal);
      return resultadoDeLaCorrida(pedido.runId, signal);
    }),

  retomarCorrida: async (jobId) => {
    if (get().isRunningInference) return null;
    let corrida: CorridaDeInferencia | null;
    try {
      corrida = await dynamicPricingService.getActiveInferenceRun(jobId);
    } catch {
      // No saber si hay una corriendo no es un error para la persona: la pantalla
      // sigue como siempre y «Ejecutar» se engancha igual a la misma corrida.
      return null;
    }
    if (!corrida) return null;
    const elegido = get().inferenceJobId;
    // Mientras se preguntaba, empezó otra espera o la persona eligió otra tienda.
    if (get().isRunningInference || (elegido && elegido !== jobId)) return null;
    const runId = corrida.id;
    return esperarComoActual(set as never, get, (signal) => resultadoDeLaCorrida(runId, signal));
  },

  cancelInference: () => {
    const abort = (get() as any)._inferenceAbort;
    if (abort) {
      abort.abort();
      set({ isRunningInference: false, _inferenceAbort: null } as any);
    }
  },

  fetchSuggestions: async (params = {}) => {
    set({ isLoadingSuggestions: true, error: null });
    try {
      const { jobId, ...rest } = params;
      const data = jobId
        ? await dynamicPricingService.getJobSuggestions(jobId, rest)
        : await dynamicPricingService.getAllSuggestions(rest);
      set({
        suggestions: data.suggestions,
        suggestionTotal: data.total,
        suggestionPage: data.page,
        suggestionTotalPages: data.totalPages,
        isLoadingSuggestions: false,
      });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando sugerencias',
        isLoadingSuggestions: false,
      });
    }
  },

  acceptSuggestion: async (id, opciones) => {
    try {
      const result = opciones
        ? await dynamicPricingService.acceptSuggestion(id, opciones)
        : await dynamicPricingService.acceptSuggestion(id);
      const siblingsRejected = result?.siblingsRejected ?? 0;
      // Sale la aceptada y también sus hermanas: el backend las descartó
      // (un item del competidor se vincula a un solo producto).
      set((s) => {
        const accepted = s.suggestions.find((sg) => sg.id === id);
        return {
          suggestions: s.suggestions.filter(
            (sg) => sg.id !== id && sg.catalogItemId !== accepted?.catalogItemId
          ),
        };
      });
      return { siblingsRejected };
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error aceptando sugerencia' });
      throw err;
    }
  },

  rejectSuggestion: async (id, motivo, opciones) => {
    try {
      if (opciones) await dynamicPricingService.rejectSuggestion(id, motivo, opciones);
      else await dynamicPricingService.rejectSuggestion(id, motivo);
      set((s) => ({ suggestions: s.suggestions.filter((sg) => sg.id !== id) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error rechazando sugerencia' });
    }
  },

  fetchInferenceStats: async (jobId) => {
    set({ isLoadingStats: true });
    try {
      const stats = await dynamicPricingService.getInferenceStats(jobId);
      set({ inferenceStats: stats, isLoadingStats: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando los números de la inferencia',
        isLoadingStats: false,
      });
    }
  },

  cleanInference: async (jobId) => {
    set({ error: null });
    try {
      await dynamicPricingService.cleanInference(jobId);
      set({
        suggestions: [],
        suggestionTotal: 0,
        suggestionPage: 1,
        suggestionTotalPages: 1,
        lastInferenceResult: null,
      });
      return true;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error limpiando inferencia' });
      return false;
    }
  },

  cleanStoreInference: async (storeId) => {
    set({ error: null });
    try {
      await dynamicPricingService.cleanStoreInference(storeId);
      set({
        suggestions: [],
        suggestionTotal: 0,
        suggestionPage: 1,
        suggestionTotalPages: 1,
        lastInferenceResult: null,
      });
      return true;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error desvinculando inferencias de la tienda' });
      return false;
    }
  },

  fixJobPrices: async (jobId) => {
    set({ error: null });
    try {
      await dynamicPricingService.fixJobPrices(jobId);
      return true;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error iniciando fix de precios' });
      return false;
    }
  },

  // Monitor Runs
  monitorRuns: [],
  monitorRunsTotal: 0,
  monitorRunsPage: 1,
  monitorRunsTotalPages: 1,
  isLoadingMonitorRuns: false,
  monitorRunsFilters: {},
  selectedMonitorRun: null,
  selectedMonitorRunAlerts: [],
  isLoadingMonitorRunDetail: false,

  setMonitorRunsFilters: (filters) =>
    set((s) => ({
      monitorRunsFilters: { ...s.monitorRunsFilters, ...filters },
    })),

  fetchMonitorRuns: async (params = {}, opts = {}) => {
    // Refresco silencioso (auto-refresh): no togglear el spinner para evitar parpadeo.
    if (!opts.silent) set({ isLoadingMonitorRuns: true, error: null });
    try {
      const state = get();
      const merged = { ...state.monitorRunsFilters, ...params };
      const data = await dynamicPricingService.getMonitorRuns(merged);
      set({
        monitorRuns: data.runs,
        monitorRunsTotal: data.total,
        monitorRunsPage: data.page,
        monitorRunsTotalPages: data.totalPages,
        isLoadingMonitorRuns: false,
      });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando historial de monitoreo',
        isLoadingMonitorRuns: false,
      });
    }
  },

  fetchMonitorRunDetail: async (id) => {
    set({ isLoadingMonitorRunDetail: true, error: null });
    try {
      const [run, alerts] = await Promise.all([
        dynamicPricingService.getMonitorRun(id),
        dynamicPricingService.getMonitorRunAlerts(id),
      ]);
      set({
        selectedMonitorRun: run,
        selectedMonitorRunAlerts: alerts,
        isLoadingMonitorRunDetail: false,
      });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando detalle',
        isLoadingMonitorRunDetail: false,
      });
    }
  },

  clearMonitorRunDetail: () => set({ selectedMonitorRun: null, selectedMonitorRunAlerts: [] }),

  isTriggeringMonitorRun: false,
  triggerMonitorRun: async (storeId, parallel) => {
    set({ isTriggeringMonitorRun: true, error: null });
    try {
      const r = await dynamicPricingService.triggerMonitorRun(storeId, parallel);
      set({ isTriggeringMonitorRun: false });
      return r?.message || 'Monitoreo encolado. El historial se actualizará al terminar.';
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error disparando monitoreo',
        isTriggeringMonitorRun: false,
      });
      return null;
    }
  },

  // ── Scheduled Tasks (calendario) ──
  scheduledTasks: [],
  scheduledAgenda: [],
  isLoadingScheduledTasks: false,
  fetchScheduledData: async (days = 14) => {
    set({ isLoadingScheduledTasks: true });
    try {
      const [tasks, agenda] = await Promise.all([
        dynamicPricingService.getScheduledTasks(),
        dynamicPricingService.getScheduledAgenda(days),
      ]);
      set({ scheduledTasks: tasks, scheduledAgenda: agenda, isLoadingScheduledTasks: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando tareas programadas',
        isLoadingScheduledTasks: false,
      });
    }
  },
  createScheduledTask: async (data) => {
    try {
      await dynamicPricingService.createScheduledTask(data);
      await get().fetchScheduledData();
      return true;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando tarea programada' });
      return false;
    }
  },
  updateScheduledTask: async (id, data) => {
    try {
      await dynamicPricingService.updateScheduledTask(id, data);
      await get().fetchScheduledData();
      return true;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando tarea programada' });
      return false;
    }
  },
  deleteScheduledTask: async (id) => {
    try {
      await dynamicPricingService.deleteScheduledTask(id);
      await get().fetchScheduledData();
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando tarea programada' });
    }
  },
  runScheduledTaskNow: async (id) => {
    try {
      await dynamicPricingService.runScheduledTaskNow(id);
      return true;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error ejecutando tarea' });
      return false;
    }
  },

  error: null,
}));
